import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter, forkJoin } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ReportingService } from '../../../../../shared/store/reporting/reporting.service';
import { ReportingApiService } from '../../../../../shared/store/reporting/reporting.api.services';
import {
  CallsPerformanceFacetDimension,
  CallsPerformanceFacetItemWithFlag,
  CallsPerformanceQuery,
  Period,
  UNASSIGNED_FACET_ID,
} from '../../../../../shared/store/reporting/types';
import { formatDate } from '../../../../../shared/helper/datehelper';

/** "All" option of the dropdowns: present in a selection, the field is not sent. */
export const CALLS_FACET_ALL_ID = -1;

/**
 * Subheader of the calls dashboard: period, then the direction / office / salesperson dropdowns.
 * Each dropdown is a facet computed by the API with the selection of the two others, so it only
 * offers values still reachable, with the number of calls behind each.
 *
 * Sole builder of the API query: it emits it (`queryChange`) with the one its dropdowns are fetched
 * with, so the dashboard rows and the dropdown counts always cover the same calls.
 */
@UntilDestroy()
@Component({
  selector: 'app-telephony-comparison-calls-subheader',
  templateUrl: './telephony-comparison-calls-subheader.component.html',
  styleUrl: './telephony-comparison-calls-subheader.component.scss',
  standalone: false,
})
export class TelephonyComparisonCallsSubheaderComponent
  implements OnInit, OnChanges
{
  @Input() startDate!: Date;
  @Output() startDateChange = new EventEmitter<Date>();

  @Input() endDate!: Date;
  @Output() endDateChange = new EventEmitter<Date>();

  @Input() period: string = 'CurrentMonth';
  @Output() periodChange = new EventEmitter<string>();

  @Input() directionIds: number[] = [];
  @Output() directionIdsChange = new EventEmitter<number[]>();

  @Input() salesTeamIds: number[] = [];
  @Output() salesTeamIdsChange = new EventEmitter<number[]>();

  @Input() salesPersonIds: number[] = [];
  @Output() salesPersonIdsChange = new EventEmitter<number[]>();

  /** The current selection as sent to the API, once per change (debounced). */
  @Output() queryChange = new EventEmitter<CallsPerformanceQuery>();

  periodLabel: string;
  periods: Period[];
  periods$: Observable<Period[]>;

  private readonly directionListSubject = new BehaviorSubject<
    CallsPerformanceFacetItemWithFlag[]
  >([]);
  private readonly salesTeamListSubject = new BehaviorSubject<
    CallsPerformanceFacetItemWithFlag[]
  >([]);
  private readonly salesPersonListSubject = new BehaviorSubject<
    CallsPerformanceFacetItemWithFlag[]
  >([]);

  directionListItems$ = this.directionListSubject.asObservable();
  salesTeamListItems$ = this.salesTeamListSubject.asObservable();
  salesPersonListItems$ = this.salesPersonListSubject.asObservable();

  private readonly refresh$ = new Subject<void>();

  // Keeps the last-seen name for each id per dimension so that a previously selected value can
  // still be rendered (greyed out) after it falls out of the cascaded facet result.
  private readonly nameCache: Record<
    CallsPerformanceFacetDimension,
    Map<number, string>
  > = {
    Direction: new Map(),
    SalesTeam: new Map(),
    SalesPerson: new Map(),
  };

  constructor(
    private readonly reportingService: ReportingService,
    private readonly reportingApiService: ReportingApiService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    // The subheader owns the "All" sentinel: an empty selection from the parent means "All". Done
    // before the inner <ng-select> reads the model, and emitted back so the parent stays in sync.
    this.initSelectionDefaults();

    this.periods$ = this.reportingService.getPeriods.value$;
    this.reportingService.getPeriods.call(new Date());

    // debounceTime(0): the period preset changes startDate and endDate back to back.
    this.refresh$
      .pipe(debounceTime(0), untilDestroyed(this))
      .subscribe(() => this.refetchAllFacets());

    this.periods$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((periods) => {
        this.periods = periods;
        this.onPeriodChange(this.period);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const watched = [
      'startDate',
      'endDate',
      'directionIds',
      'salesTeamIds',
      'salesPersonIds',
    ];
    if (watched.some((k) => changes[k])) {
      this.refresh$.next();
    }
  }

  onPeriodChange(newValue: string) {
    this.period = newValue;
    this.periodChange.emit(newValue);
    this.periods.forEach((period) => {
      if (period.value == newValue) {
        this.startDate = new Date(period.startDate);
        this.startDateChange.emit(this.startDate);

        this.endDate = new Date(period.endDate);
        this.endDateChange.emit(this.endDate);
      }
    });
    this.periodLabel = formatPeriodLabel(
      new Date(this.startDate),
      new Date(this.endDate),
    );
    this.refresh$.next();
  }

  onDirectionIdsChange() {
    this.directionIds = this.normalizeSelection(this.directionIds);
    this.directionIdsChange.emit(this.directionIds);
    this.refresh$.next();
  }

  onSalesTeamIdsChange() {
    this.salesTeamIds = this.normalizeSelection(this.salesTeamIds);
    this.salesTeamIdsChange.emit(this.salesTeamIds);
    this.refresh$.next();
  }

  onSalesPersonIdsChange() {
    this.salesPersonIds = this.normalizeSelection(this.salesPersonIds);
    this.salesPersonIdsChange.emit(this.salesPersonIds);
    this.refresh$.next();
  }

  private initSelectionDefaults(): void {
    if (!this.directionIds?.length) {
      this.directionIds = [CALLS_FACET_ALL_ID];
      this.directionIdsChange.emit(this.directionIds);
    }
    if (!this.salesTeamIds?.length) {
      this.salesTeamIds = [CALLS_FACET_ALL_ID];
      this.salesTeamIdsChange.emit(this.salesTeamIds);
    }
    if (!this.salesPersonIds?.length) {
      this.salesPersonIds = [CALLS_FACET_ALL_ID];
      this.salesPersonIdsChange.emit(this.salesPersonIds);
    }
  }

  /** "All" picked last replaces the selection; any other pick drops "All"; nothing left = "All". */
  private normalizeSelection(ids: number[]): number[] {
    if (ids.slice(-1)[0] === CALLS_FACET_ALL_ID) return [CALLS_FACET_ALL_ID];

    const withoutAll = ids.filter((id) => id !== CALLS_FACET_ALL_ID);
    return withoutAll.length > 0 ? withoutAll : [CALLS_FACET_ALL_ID];
  }

  private refetchAllFacets(): void {
    if (!this.startDate || !this.endDate) return;

    const query = this.buildQuery();
    this.queryChange.emit(query);

    forkJoin({
      Direction: this.fetchFacet('Direction', query),
      SalesTeam: this.fetchFacet('SalesTeam', query),
      SalesPerson: this.fetchFacet('SalesPerson', query),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ Direction, SalesTeam, SalesPerson }) => {
        this.directionListSubject.next(
          this.buildFacetList(
            'Direction',
            Direction,
            this.directionIds,
            'DASHBOARD_LBL_CALLS_DIRECTION_ALL',
          ),
        );
        this.salesTeamListSubject.next(
          this.buildFacetList(
            'SalesTeam',
            SalesTeam,
            this.salesTeamIds,
            'DASHBOARD_LBL_SALESTEAM_ALL',
          ),
        );
        this.salesPersonListSubject.next(
          this.buildFacetList(
            'SalesPerson',
            SalesPerson,
            this.salesPersonIds,
            'DASHBOARD_LBL_SALESPERSON_ALL',
          ),
        );
      });
  }

  // ng-select can't bind null, so the API's null "unassigned" id is swapped for the UI sentinel
  // here, at the component boundary. It travels back to the API as null in buildQuery.
  private fetchFacet(
    dimension: CallsPerformanceFacetDimension,
    query: CallsPerformanceQuery,
  ): Observable<CallsPerformanceFacetItemWithFlag[]> {
    return this.reportingApiService
      .getCallsPerformanceFacetItems({ ...query, dimension })
      .pipe(
        map((items) =>
          items.map((item) => ({
            ...item,
            id: item.id ?? UNASSIGNED_FACET_ID,
          })),
        ),
      );
  }

  /**
   * The whole selection, the same for the rows and every dropdown: the back leaves out the requested
   * dimension's own one. Local days, no UTC conversion: the back applies the user's time zone.
   */
  private buildQuery(): CallsPerformanceQuery {
    return {
      startDate: formatDate(this.startDate, 'ToInverseSqlDate'),
      endDate: formatDate(this.endDate, 'ToInverseSqlDate'),
      directions: toQueryIds(this.directionIds).filter(
        (id): id is number => id !== null,
      ),
      salesTeamIds: toQueryIds(this.salesTeamIds),
      salesPersonIds: toQueryIds(this.salesPersonIds),
    };
  }

  private buildFacetList(
    dimension: CallsPerformanceFacetDimension,
    apiItems: CallsPerformanceFacetItemWithFlag[],
    selectedIds: number[],
    allLabelKey: string,
  ): CallsPerformanceFacetItemWithFlag[] {
    const allOption: CallsPerformanceFacetItemWithFlag = {
      id: CALLS_FACET_ALL_ID,
      name: this.translateService.instant(allLabelKey),
      count: apiItems.reduce((sum, i) => sum + i.count, 0),
    };
    return [
      allOption,
      ...this.mergeWithSelected(dimension, apiItems, selectedIds),
    ];
  }

  private mergeWithSelected(
    dimension: CallsPerformanceFacetDimension,
    apiItems: CallsPerformanceFacetItemWithFlag[],
    selectedIds: number[],
  ): CallsPerformanceFacetItemWithFlag[] {
    const cache = this.nameCache[dimension];
    apiItems.forEach((item) => cache.set(item.id, item.name));

    const selected = (selectedIds ?? []).filter(
      (id) => id !== CALLS_FACET_ALL_ID,
    );
    const apiIds = new Set(apiItems.map((i) => i.id));

    const visible = apiItems
      .filter((item) => item.count > 0 || selected.includes(item.id))
      .sort((a, b) => b.count - a.count);

    const missing: CallsPerformanceFacetItemWithFlag[] = selected
      .filter((id) => !apiIds.has(id))
      .map((id) => ({
        id,
        name: cache.get(id) ?? '',
        count: 0,
        disabled: true,
      }));

    return [...visible, ...missing];
  }
}

/** "All" selected = empty list (no key sent); the unassigned UI sentinel goes back to null. */
function toQueryIds(ids: number[]): (number | null)[] {
  if (!ids || ids.includes(CALLS_FACET_ALL_ID)) return [];
  return ids.map((id) => (id === UNASSIGNED_FACET_ID ? null : id));
}

function formatPeriodLabel(startDate: Date, endDate: Date) {
  const fullOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const monthOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  };
  const dayOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
  };
  let periodLabel: string = '';
  if (startDate.getFullYear() == endDate.getFullYear()) {
    periodLabel =
      'Du ' +
      startDate.toLocaleDateString('fr-FR', monthOptions) +
      ' au ' +
      endDate.toLocaleDateString('fr-FR', fullOptions);

    if (startDate.getMonth() == endDate.getMonth()) {
      periodLabel =
        'Du ' +
        startDate.toLocaleDateString('fr-FR', dayOptions) +
        ' au ' +
        endDate.toLocaleDateString('fr-FR', fullOptions);

      if (startDate.getDate() == endDate.getDate()) {
        periodLabel =
          'Le ' + startDate.toLocaleDateString('fr-FR', fullOptions);
      }
    }
  }

  if (startDate.getFullYear() != endDate.getFullYear()) {
    periodLabel =
      'Du ' +
      startDate.toLocaleDateString('fr-FR', fullOptions) +
      ' au ' +
      endDate.toLocaleDateString('fr-FR', fullOptions);
  }
  return periodLabel;
}
